import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { appStore } from '@databases/redux/store';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';

export type DatabaseAifcAnalyticsSnapshot = {
  elementKeys: string[];
};

const getCellElementKeys = (
  databaseDraft: ReturnType<typeof selectDatabaseDraftByContentId>
): string[] => {
  const keys: string[] = [];
  for (const entryId of databaseDraft.entries) {
    const values = databaseDraft.entriesById[entryId]?.values ?? {};
    for (const fieldId of Object.keys(values)) {
      // If value is not empty (allow 0 and false), add the cell to the keys
      if (values[fieldId].value != null && values[fieldId].value !== '') {
        keys.push(`cell:${entryId}:${fieldId}`);
      }
    }
  }
  return keys;
};

const getViewElementKeys = (
  database: ReturnType<typeof selectDatabaseByContentIdRequired>
): string[] => {
  const keys: string[] = [];

  for (const view of database.views) {
    const viewId = view?.id;
    if (!viewId) {
      continue;
    }

    // add each view as one element
    keys.push(`view:${viewId}`);

    // each sort on field counts as one element
    for (const sort of view.settings?.sorts ?? []) {
      keys.push(`view-setting:${viewId}:sort:${sort.fieldId}`);
    }

    // each filter on a view counts as one element
    if (view.settings?.filter) {
      keys.push(`view-setting:${viewId}:filter:${view.settings.filter.id}`);
    }

    // each hidden field counts as one element
    for (const [fieldId, visible] of Object.entries(view.settings?.fields ?? {})) {
      if (visible === false) {
        keys.push(`view-setting:${viewId}:hidden-field:${fieldId}`);
      }
    }
  }

  return keys;
};

export const buildDatabaseAifcAnalyticsSnapshot = (
  contentId: string
): DatabaseAifcAnalyticsSnapshot => {
  const state = appStore.getState();
  const databaseDraft = selectDatabaseDraftByContentId(state, {
    contentId,
    includeDeleted: false,
  });
  const database = selectDatabaseByContentIdRequired(state, { contentId });

  const elementKeys = [
    ...getCellElementKeys(databaseDraft),
    ...getViewElementKeys(database),
  ];

  return { elementKeys };
};

export const getRetainedElementCount = (
  baselineSnapshot: DatabaseAifcAnalyticsSnapshot,
  currentSnapshot: DatabaseAifcAnalyticsSnapshot
): number => {
  const currentElements = new Set(currentSnapshot.elementKeys);
  return baselineSnapshot.elementKeys.filter((elementKey) =>
    currentElements.has(elementKey)
  ).length;
};
